import { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";

const ManageFields = () => {
  const [fields, setFields] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [form, setForm] = useState({
    FieldName: "",
    FieldType: "",
    FieldValidation: "",
    RequiredCheck: false,
    Priority: "",
    fieldNumber: "",
    DropDownValues: [],
  });
  const navigate = useNavigate();

  // Field Values
  const [values, setValues] = useState({});
  const [valueForm, setValueForm] = useState({});
  const [editingValue, setEditingValue] = useState(null);
  const [activeFieldForValues, setActiveFieldForValues] = useState(null);

  // Client Selection
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const companyId = localStorage.getItem("company_id");
  const userType = localStorage.getItem("user_type");

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await api.get("/agents/clients-rights");
        const sortedClients = res.data.sort((a, b) =>
          a.company_name.localeCompare(b.company_name, "en", { sensitivity: "base" })
        );
        setClients(sortedClients);
      } catch (err) {
        console.error("Error fetching clients:", err);
      }
    };
    fetchClients();
  }, []);

  // Auto-select client for Client user or single company
  useEffect(() => {
    if (userType === "Client") {
      setSelectedClient(companyId);
    } else if ((userType === "Super-Admin" || userType === "Admin") && clients.length === 1) {
      setSelectedClient(clients[0].company_id);
    }
  }, [userType, companyId, clients]);

  // Fetch fields when client changes
  useEffect(() => {
    if (selectedClient) fetchFields();
  }, [selectedClient]);

  const fetchFields = async () => {
    if (!selectedClient) return;
    try {
      const res = await api.get(`/field_master/fields/${selectedClient}`);
      setFields(res.data);
    } catch (err) {
      console.error("Error fetching fields:", err);
    }
  };

  const fetchValues = async (fieldId) => {
    try {
      const res = await api.get(`/field_master/field_values/${selectedClient}/${fieldId}`);
      setValues(prev => ({ ...prev, [fieldId]: res.data }));
    } catch (err) {
      console.error("Error fetching field values:", err);
    }
  };

  const resetForm = () => {
    setForm({
      FieldName: "",
      FieldType: "",
      FieldValidation: "",
      RequiredCheck: false,
      Priority: "",
      fieldNumber: "",
      DropDownValues: [],
    });
    setEditingField(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "FieldType" && value === "DropDown") {
      setForm(prev => ({ ...prev, [name]: value, DropDownValues: prev.DropDownValues || [""] }));
    } else {
      setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      alert("Please select a client first.");
      return;
    }

    const payload = {
      ...form,
      Priority: form.Priority ? parseInt(form.Priority, 10) : null,
      fieldNumber: form.fieldNumber ? parseInt(form.fieldNumber, 10) : null,
      RequiredCheck: form.RequiredCheck ? 1 : 0,
    };

    try {
      let fieldId = editingField ? editingField.id : null;

      if (editingField) {
        await api.put(`/field_master/fields/${selectedClient}/${fieldId}`, payload);
        if (form.FieldType === "DropDown") {
          // Fetch existing values first
          const existing = await api.get(`/field_master/field_values/${selectedClient}/${fieldId}`);
          const existingMap = new Map(existing.data.map(v => [v.id, v.FieldValueName]));

          // 1. Update or add new
          for (const val of form.DropDownValues.filter(v => v.trim() !== "")) {
            const found = [...existingMap.entries()].find(([, name]) => name === val);
            if (!found) {
              // New → insert
              await api.post(`/field_master/field_values/${selectedClient}`, {
                FieldValueName: val,
                FieldId: fieldId,
              });
            }
          }

          // 2. Delete removed
          for (const [id, name] of existingMap.entries()) {
            if (!form.DropDownValues.includes(name)) {
              await api.delete(`/field_master/field_values/${selectedClient}/${fieldId}/${id}`);
            }
          }
        }
        alert("Field updated successfully!");
      } else {
        const res = await api.post(`/field_master/fields/${selectedClient}`, payload);
        fieldId = res.data.field_id;
        if (form.FieldType === "DropDown" && form.DropDownValues.length > 0) {
          for (const val of form.DropDownValues.filter(v => v.trim() !== "")) {
            await api.post(`/field_master/field_values/${selectedClient}`, { FieldValueName: val, FieldId: fieldId });
          }
        }
        alert("Field added successfully!");
      }

      resetForm();
      setShowModal(false);
      fetchFields();
    } catch (err) {
      console.error(err);
      alert("Error: " + (err.response?.data?.detail || "Failed to save field"));
    }
  };

  const handleEdit = async (field) => {
      let dropDownValues = [];
      if (field.FieldType === "DropDown") {
        try {
          const res = await api.get(`/field_master/field_values/${selectedClient}/${field.id}`);
          dropDownValues = res.data.map(v => v.FieldValueName);
        } catch (err) {
          console.error("Failed to fetch dropdown values", err);
        }
      }

      setForm({
        ...field,
        RequiredCheck: field.RequiredCheck === 1 || field.RequiredCheck === "1" || field.RequiredCheck === true,
        Priority: field.Priority || "",
        fieldNumber: field.fieldNumber || "",
        DropDownValues: dropDownValues.length > 0 ? dropDownValues : [],
      });
      setEditingField(field);
      setShowModal(true);
  };


  const handleDelete = async (fieldId) => {
    if (!window.confirm("Delete this field?")) return;
    try {
      await api.delete(`/field_master/fields/${selectedClient}/${fieldId}`);
      setFields(prev => prev.filter(f => f.id !== fieldId));
    } catch (err) {
      alert("Failed to delete field");
    }
  };

  // --- Field Values ---
  const handleValueSubmit = async (fieldId) => {
    const payload = { FieldValueName: valueForm.FieldValueName, FieldId: fieldId };
    try {
      if (editingValue) {
        await api.put(`/field_master/field_values/${selectedClient}/${editingValue.id}`, payload);
        alert("Value updated!");
      } else {
        await api.post(`/field_master/field_values/${selectedClient}`, payload);
        alert("Value added!");
      }
      setValueForm({});
      setEditingValue(null);
      fetchValues(fieldId);
    } catch {
      alert("Failed to save value");
    }
  };

  const handleValueEdit = (fieldId, val) => {
    setValueForm({ FieldValueName: val.FieldValueName });
    setEditingValue({ ...val, FieldId: fieldId });
    setActiveFieldForValues(fieldId);
  };

  const handleValueDelete = async (fieldId, valueId) => {
    if (!window.confirm("Delete this value?")) return;
    try {
      await api.delete(`/field_master/field_values/${selectedClient}/${fieldId}/${valueId}`);
      fetchValues(fieldId);
    } catch {
      alert("Failed to delete value");
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        {/* Client Dropdown for Super-Admin/Admin */}
        {(userType === "Super-Admin" || userType === "Admin") && (
          <div className="row mb-4">
            <div className="col-md-4">
              <label className="form-label fw-semibold">Select Client</label>
              <select
                className="form-select"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="">-- Select Client --</option>
                {clients.map((client) => (
                  <option key={client.company_id} value={String(client.company_id)}>
                    {client.company_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Fields Section */}
        {selectedClient ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h3>Manage Fields</h3>
              </div>
              <div>
              <button
                type="button"
                className="btn btn-outline-primary rounded-3 me-2"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
              <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
                + Add Field
              </button>
              </div>
            </div>

            {/* Fields Table */}
            <div className="table-responsive" style={{ maxHeight: 500, overflowY: "auto" }}>
              <table className="table table-hover table-striped table-bordered align-middle shadow-sm">
                <thead className="table-dark sticky-top">
                  <tr>
                    <th className="text-center">Name</th>
                    <th className="text-center">Type</th>
                    <th className="text-center">Validation</th>
                    <th className="text-center">Required</th>
                    <th className="text-center">Priority</th>
                    <th className="text-center">Field</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.length > 0 ? (
                    fields.map((field) => (
                      <tr key={field.id}>
                        <td className="text-center">{field.FieldName}</td>
                        <td className="text-center">{field.FieldType}</td>
                        <td className="text-center">{field.FieldValidation}</td>
                        <td className="text-center">
                          {field.RequiredCheck === 1 || field.RequiredCheck === "1" || field.RequiredCheck === true ? "Yes" : "No"}
                        </td>
                        <td className="text-center">{field.Priority}</td>
                        <td className="text-center">{field.fieldNumber}</td>
                        <td className="text-center">
                          <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleEdit(field)}>✏ Edit</button>
                          <button className="btn btn-sm btn-outline-danger me-2" onClick={() => handleDelete(field.id)}>🗑 Delete</button>
                          <button
                            className="btn btn-sm btn-outline-info"
                            disabled={field.FieldType !== "DropDown"}
                            onClick={() => {
                              if (field.FieldType === "DropDown") {
                                fetchValues(field.id);
                                setActiveFieldForValues(activeFieldForValues === field.id ? null : field.id);
                              }
                            }}
                          >📂 Values</button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="7" className="text-center text-muted py-3">No fields found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Field Values Section */}
            {activeFieldForValues && (
              <div className="mt-4">
                <h5>Field Values for {fields.find(f => f.id === activeFieldForValues)?.FieldName}</h5>
                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    placeholder="Enter value"
                    value={valueForm.FieldValueName || ""}
                    onChange={(e) => setValueForm({ FieldValueName: e.target.value })}
                  />
                  <button className="btn btn-success" onClick={() => handleValueSubmit(activeFieldForValues)}>
                    {editingValue ? "Update" : "Add"}
                  </button>
                </div>
                <ul className="list-group">
                  {(values[activeFieldForValues] || []).map(val => (
                    <li key={val.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {val.FieldValueName}
                      <span>
                        <button className="btn btn-sm btn-outline-warning me-2" onClick={() => handleValueEdit(activeFieldForValues, val)}>✏</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleValueDelete(activeFieldForValues, val.id)}>🗑</button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Modal */}
            {showModal && (
              <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">{editingField ? "Edit Field" : "Add Field"}</h5>
                      <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                    </div>
                    <div className="modal-body">
                      <form className="row g-3" onSubmit={handleSubmit}>
                        <div className="col-md-6">
                          <label className="form-label">Field Name *</label>
                          <input name="FieldName" className="form-control" value={form.FieldName} onChange={handleChange} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Field Type *</label>
                          <select name="FieldType" className="form-select" value={form.FieldType} onChange={handleChange} required>
                            <option value="">-- Select Field Type --</option>
                            <option value="TextBox">Text Box</option>
                            <option value="TextArea">Text Area</option>
                            <option value="DropDown">Drop Down</option>
                          </select>
                        </div>

                        {form.FieldType === "DropDown" && (
                          <div>
                            {form.DropDownValues.map((val, idx) => (
                              <div key={idx} className="d-flex gap-2 mb-2">
                                <input
                                  type="text"
                                  value={val}
                                  onChange={(e) => {
                                    const newValues = [...form.DropDownValues];
                                    newValues[idx] = e.target.value;
                                    setForm({ ...form, DropDownValues: newValues });
                                  }}
                                  className="form-control"
                                  placeholder={`Value ${idx + 1}`}
                                />
                                <button type="button" className="btn btn-danger" onClick={() => setForm({ ...form, DropDownValues: form.DropDownValues.filter((_, i) => i !== idx) })}>X</button>
                              </div>
                            ))}
                            <button type="button" className="btn btn-primary" onClick={() => setForm({ ...form, DropDownValues: [...form.DropDownValues, ""] })}>+ Add Value</button>
                          </div>
                        )}

                        <div className="col-md-6">
                          <label className="form-label">Validation</label>
                          <select name="FieldValidation" className="form-select" value={form.FieldValidation} onChange={handleChange}>
                            <option value="">-- Select Validation --</option>
                            <option value="Numeric">Numeric</option>
                            <option value="Char">Character</option>
                            <option value="Alphanumeric">Alphanumeric</option>
                            <option value="Datepicker">Datepicker</option>
                            <option value="Phone">Phone</option>
                          </select>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                          <div className="form-check mt-4">
                            <input type="checkbox" className="form-check-input" name="RequiredCheck" checked={form.RequiredCheck} onChange={handleChange} />
                            <label className="form-check-label">Required</label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Priority</label>
                          <input name="Priority" className="form-control" value={form.Priority} onChange={handleChange} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Field Number</label>
                          <input name="fieldNumber" className="form-control" value={form.fieldNumber} onChange={handleChange} />
                        </div>
                        <div className="col-12">
                          <button type="submit" className="btn btn-primary">{editingField ? "Update" : "Save"}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {showModal && <div className="modal-backdrop fade show"></div>}
          </>
        ) : (
          (userType === "Super-Admin" || userType === "Admin") && (
            <p className="text-muted">Please select a client to manage fields.</p>
          )
        )}
      </div>
    </div>
  );
};

export default ManageFields;
